/**
 * Migration slice (PX-W7.5, TASK-501F9B15) — the PX-W6.3 wiring pattern
 * copied from `features/phase-gates/`, extended to SIX independent
 * sub-states (INV-5.2): plan · runs · reconciliation · go/no-go · rollback ·
 * trail, each carrying its own `loading` / `error` so a failure in one never
 * clears another's data and never becomes the page's error.
 *
 * `blocked` is set ONLY from a 403/404 on the PLANS read (INV-5.3); every
 * other thunk's rejection writes into its OWN sub-state's error and touches
 * neither `blocked` nor the page-level `error`. Exactly one of `blocked` or
 * `error` is ever non-null.
 *
 * `goLiveInvalidated` is rendered FROM THE FIELD on `RollbackStateView` /
 * `MigrationTrailSummary` and from nothing else — no selector, reducer or
 * thunk here computes it, negates a status to get it, or derives it from
 * `MigrationRunStatus` (INV-5.5; proven by `slices/goLiveInvalidated.test.ts`).
 *
 * Realtime is refetch-on-invalidate on the EXISTING `realtime/invalidateActivity`
 * action (Decision 12, INV-5.7), the same typed-alias idiom
 * `features/intakes/slices/intakesSlice.ts` (`intakesBusFrame`) and
 * `features/activity-feed/slices/activityFeedSlice.ts` already use: this
 * slice `addCase`s a LOCAL `createAction` sharing that literal type string
 * and guards on the frame's `projectId` — `realtimeSlice.ts` is never edited
 * and no nonce family is introduced.
 */

import { createAction, createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  markServerChanged,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { migrationService } from '../services/migrationService.http'
import type {
  ActivityRow,
  AssignDifferenceOwnerInput,
  AuditPage,
  ConfirmGuardrailsInput,
  CreateMigrationPlanInput,
  DisposeDifferenceInput,
  ExecuteRollbackInput,
  FeatureBlocked,
  GoNoGoRecord,
  GuardrailStatus,
  KeyedFeatureError,
  MigrationPlanRow,
  MigrationRunRow,
  MigrationTrailSummary,
  ProposeDifferenceInput,
  ReconciliationReport,
  ReconciliationTraceEntry,
  RecordGoNoGoInput,
  RollbackStateView
} from '../types'

/** One independently-loading, independently-failing slice of feature state. */
interface SubState<T> {
  data: T | null
  loading: boolean
  error: KeyedFeatureError | null
}

const emptySubState = <T>(): SubState<T> => ({ data: null, loading: false, error: null })

export interface TrailState {
  summary: SubState<MigrationTrailSummary>
  audit: SubState<AuditPage>
  activity: SubState<ActivityRow[]>
  trace: SubState<ReconciliationTraceEntry[]>
  /** The guardrails-confirm act's own refusal — scoped separately from the four reads above. */
  guardrailError: KeyedFeatureError | null
}

const emptyTrail = (): TrailState => ({
  summary: emptySubState(),
  audit: emptySubState(),
  activity: emptySubState(),
  trace: emptySubState(),
  guardrailError: null
})

/** The four trail reads, each deduped by its own `trailInFlight` entry. */
type TrailPart = 'summary' | 'audit' | 'activity' | 'trace'

export interface MigrationState extends FetchCache {
  projectId: string | null
  selectedPlanId: string | null
  selectedRunId: string | null
  /** The primary read (INV-5.3's blocked source). */
  plans: MigrationPlanRow[]
  creatingPlan: boolean
  createPlanError: KeyedFeatureError | null
  runs: SubState<MigrationRunRow[]>
  reconciliation: SubState<ReconciliationReport>
  goNoGo: SubState<GoNoGoRecord>
  /** Decision 8: a 404 on the go/no-go read means "no decision yet" — NOT `blocked`. */
  goNoGoMissing: boolean
  rollback: SubState<RollbackStateView>
  trail: TrailState
  /**
   * The planId in flight for each of the four trail reads — kept OUTSIDE
   * `TrailState` on purpose so `emptyTrail()` (`migrationClosed` /
   * `planSelected`) never resets it: a StrictMode double-mount effect for
   * the SAME plan must still be vetoed by the second dispatch's `condition`
   * (deferred-findings AC-2 / batch contract C-3).
   */
  trailInFlight: Record<TrailPart, string | null>
  /** The diffId / act name currently in flight, for a panel's own spinner. */
  mutating: string | null
  /** PAGE-level: the plans read only (INV-5.3). */
  error: KeyedFeatureError | null
  /** PAGE-level: the plans read only (INV-5.3). */
  blocked: FeatureBlocked | null
  invalidationNonce: number
}

const initialState: MigrationState = {
  projectId: null,
  selectedPlanId: null,
  selectedRunId: null,
  plans: [],
  creatingPlan: false,
  createPlanError: null,
  runs: emptySubState(),
  reconciliation: emptySubState(),
  goNoGo: emptySubState(),
  goNoGoMissing: false,
  rollback: emptySubState(),
  trail: emptyTrail(),
  trailInFlight: { summary: null, audit: null, activity: null, trace: null },
  mutating: null,
  error: null,
  blocked: null,
  invalidationNonce: 0,
  ...initialFetchCache
}

interface ProjectArg {
  orgId: string
  projectId: string
}

interface PlanArg extends ProjectArg {
  planId: string
}

interface RunArg extends PlanArg {
  runId: string
}

interface DiffArg extends RunArg {
  diffId: string
}

/** Map any thrown value to a keyed refusal, optionally scoped to a row — always carries the real status. */
const toMigError = (error: unknown, scope?: string): KeyedFeatureError => {
  const a = isApiError(error) ? error : toApiError(error)
  return { scope, code: a.backendKey, message: a.message, status: a.status }
}

/** The fallback shape for the rare case a rejected action carries no `payload` at all. */
const unknownError = (message: string | undefined): KeyedFeatureError => ({
  status: 0,
  message: message ?? 'Unknown error'
})

/**
 * Adapts a `KeyedFeatureError` into the real `ApiError` every `<ErrorPanel>`
 * call site must be handed (`forbiddenAudit.test.tsx` fails a hand-built
 * literal) — the field-name adapter `asApiError` (`@/services/apiError`)
 * expects (`key`, not `code`) is done here once rather than at six call
 * sites.
 */
export const toDisplayError = (error: KeyedFeatureError): ApiError => ({
  status: error.status,
  backendKey: error.code,
  message: error.message
})

// ---------------------------------------------------------------------------
// Thunks — plans (AC-1, AC-2)
// ---------------------------------------------------------------------------

/** Mount: every plan on the project (`GET .../migration-plans`). */
export const openMigration = createAsyncThunk<
  MigrationPlanRow[],
  ProjectArg,
  { rejectValue: KeyedFeatureError }
>(
  'migration/open',
  async ({ orgId, projectId }, { rejectWithValue }) => {
    try {
      return await migrationService.listPlans(orgId, projectId)
    } catch (error) {
      return rejectWithValue(toMigError(error))
    }
  },
  {
    condition: ({ projectId }, { getState }) => {
      const { migration } = getState() as { migration: MigrationState }
      return shouldFetch(migration, projectId)
    },
    // A vetoed open is still an OPEN: it dispatches `rejected` with
    // `meta.condition`, and the reducer re-asserts the project on screen
    // (TASK-3D6DB8B6 fix cycle 3 — see the rejected case). No request is sent.
    dispatchConditionRejection: true
  }
)

/** AC-1's create-plan action on an empty project. Selects the new plan. */
export const createMigrationPlan = createAsyncThunk<
  MigrationPlanRow,
  ProjectArg & { input: CreateMigrationPlanInput },
  { rejectValue: KeyedFeatureError }
>('migration/createPlan', async ({ orgId, projectId, input }, { rejectWithValue }) => {
  try {
    return await migrationService.createPlan(orgId, projectId, input)
  } catch (error) {
    return rejectWithValue(toMigError(error))
  }
})

// ---------------------------------------------------------------------------
// Thunks — runs (AC-1)
// ---------------------------------------------------------------------------

/** The selected plan's runs, with per-object counts. */
export const loadRuns = createAsyncThunk<
  MigrationRunRow[],
  PlanArg,
  { rejectValue: KeyedFeatureError }
>('migration/loadRuns', async ({ orgId, projectId, planId }, { rejectWithValue }) => {
  try {
    return await migrationService.listRuns(orgId, projectId, planId)
  } catch (error) {
    return rejectWithValue(toMigError(error))
  }
})

/** One run, re-read authoritatively — swapped into `runs.data` by id. Read-only. */
export const loadRun = createAsyncThunk<MigrationRunRow, RunArg, { rejectValue: KeyedFeatureError }>(
  'migration/loadRun',
  async ({ orgId, projectId, planId, runId }, { rejectWithValue }) => {
    try {
      return await migrationService.getRun(orgId, projectId, planId, runId)
    } catch (error) {
      return rejectWithValue(toMigError(error))
    }
  }
)

// ---------------------------------------------------------------------------
// Thunks — reconciliation (AC-3, AC-4; INV-5.6)
// ---------------------------------------------------------------------------

export const loadReconciliation = createAsyncThunk<
  ReconciliationReport,
  RunArg,
  { rejectValue: KeyedFeatureError }
>('migration/loadReconciliation', async ({ orgId, projectId, planId, runId }, { rejectWithValue }) => {
  try {
    return await migrationService.getReconciliation(orgId, projectId, planId, runId)
  } catch (error) {
    return rejectWithValue(toMigError(error))
  }
})

/** Verb 1 of 3 (INV-5.6). Rejects scoped by `diffId`; the report is left untouched. */
export const assignDifferenceOwner = createAsyncThunk<
  ReconciliationReport,
  DiffArg & { input: AssignDifferenceOwnerInput },
  { rejectValue: KeyedFeatureError }
>(
  'migration/assignDifferenceOwner',
  async ({ orgId, projectId, planId, runId, diffId, input }, { rejectWithValue }) => {
    try {
      return await migrationService.assignDifferenceOwner(orgId, projectId, planId, runId, diffId, input)
    } catch (error) {
      return rejectWithValue(toMigError(error, diffId))
    }
  }
)

/** Verb 2 of 3 — a SUGGESTION only; can never itself reach a terminal state. */
export const proposeDifference = createAsyncThunk<
  ReconciliationReport,
  DiffArg & { input: ProposeDifferenceInput },
  { rejectValue: KeyedFeatureError }
>(
  'migration/proposeDifference',
  async ({ orgId, projectId, planId, runId, diffId, input }, { rejectWithValue }) => {
    try {
      return await migrationService.proposeDifference(orgId, projectId, planId, runId, diffId, input)
    } catch (error) {
      return rejectWithValue(toMigError(error, diffId))
    }
  }
)

/** Verb 3 of 3, terminal. A refusal (e.g. an unassigned difference) is scoped by `diffId`. */
export const disposeDifference = createAsyncThunk<
  ReconciliationReport,
  DiffArg & { input: DisposeDifferenceInput },
  { rejectValue: KeyedFeatureError }
>(
  'migration/disposeDifference',
  async ({ orgId, projectId, planId, runId, diffId, input }, { rejectWithValue }) => {
    try {
      return await migrationService.disposeDifference(orgId, projectId, planId, runId, diffId, input)
    } catch (error) {
      return rejectWithValue(toMigError(error, diffId))
    }
  }
)

// ---------------------------------------------------------------------------
// Thunks — go/no-go (AC-5; Decisions 7–8)
// ---------------------------------------------------------------------------

/**
 * The CURRENT record. A 404 means no decision has been taken yet — Decision
 * 8 renders that as the panel's empty state, never the page's `blocked`.
 */
export const loadGoNoGo = createAsyncThunk<
  GoNoGoRecord,
  PlanArg,
  { rejectValue: KeyedFeatureError }
>('migration/loadGoNoGo', async ({ orgId, projectId, planId }, { rejectWithValue }) => {
  try {
    return await migrationService.getGoNoGo(orgId, projectId, planId)
  } catch (error) {
    return rejectWithValue(toMigError(error))
  }
})

/** Records the verdict, then re-reads the stored record (Decision 7). */
export const recordGoNoGo = createAsyncThunk<
  void,
  PlanArg & { input: RecordGoNoGoInput },
  { rejectValue: KeyedFeatureError }
>('migration/recordGoNoGo', async ({ orgId, projectId, planId, input }, { dispatch, rejectWithValue }) => {
  try {
    await migrationService.recordGoNoGo(orgId, projectId, planId, input)
    await dispatch(loadGoNoGo({ orgId, projectId, planId }))
  } catch (error) {
    return rejectWithValue(toMigError(error))
  }
})

// ---------------------------------------------------------------------------
// Thunks — rollback (AC-6; Decisions 4–6)
// ---------------------------------------------------------------------------

export const loadRollback = createAsyncThunk<
  RollbackStateView,
  RunArg,
  { rejectValue: KeyedFeatureError }
>('migration/loadRollback', async ({ orgId, projectId, planId, runId }, { rejectWithValue }) => {
  try {
    return await migrationService.getRollback(orgId, projectId, planId, runId)
  } catch (error) {
    return rejectWithValue(toMigError(error))
  }
})

/**
 * The UI hides this action when the caller's grant or the last-known status
 * makes it implausible; it never decides the refusal (Decision 6). On
 * rejection the state is left byte-identical.
 */
export const executeRollback = createAsyncThunk<
  RollbackStateView,
  RunArg & { input: ExecuteRollbackInput },
  { rejectValue: KeyedFeatureError }
>('migration/executeRollback', async ({ orgId, projectId, planId, runId, input }, { rejectWithValue }) => {
  try {
    return await migrationService.executeRollback(orgId, projectId, planId, runId, input)
  } catch (error) {
    return rejectWithValue(toMigError(error))
  }
})

// ---------------------------------------------------------------------------
// Thunks — trail (AC-7)
// ---------------------------------------------------------------------------

/** A trail thunk's `condition`: veto a same-plan dispatch while one is in flight (deferred-findings AC-2). */
const trailNotInFlight =
  (part: TrailPart) =>
  ({ planId }: { planId: string }, { getState }: { getState: () => unknown }): boolean =>
    (getState() as { migration: MigrationState }).migration.trailInFlight[part] !== planId

/** Clear `trailInFlight[part]` only if it still names the settling request's plan. */
const settleTrail = (state: MigrationState, part: TrailPart, planId: string): void => {
  if (state.trailInFlight[part] === planId) state.trailInFlight[part] = null
}

export const loadTrailSummary = createAsyncThunk<
  MigrationTrailSummary,
  PlanArg,
  { rejectValue: KeyedFeatureError }
>(
  'migration/loadTrailSummary',
  async ({ orgId, projectId, planId }, { rejectWithValue }) => {
    try {
      return await migrationService.getTrailSummary(orgId, projectId, planId)
    } catch (error) {
      return rejectWithValue(toMigError(error))
    }
  },
  {
    condition: trailNotInFlight('summary')
  }
)

export const loadAuditTrail = createAsyncThunk<
  AuditPage,
  PlanArg & { limit?: number; cursor?: string },
  { rejectValue: KeyedFeatureError }
>(
  'migration/loadAuditTrail',
  async ({ orgId, projectId, planId, limit, cursor }, { rejectWithValue }) => {
    try {
      return await migrationService.getAuditTrail(orgId, projectId, planId, { limit, cursor })
    } catch (error) {
      return rejectWithValue(toMigError(error))
    }
  },
  {
    // A cursor request is an explicit next-page fetch, never a duplicate
    // mount dispatch — the dedupe veto applies only to the first page.
    condition: (arg, api) => arg.cursor !== undefined || trailNotInFlight('audit')(arg, api)
  }
)

export const loadActivityTrail = createAsyncThunk<
  ActivityRow[],
  PlanArg,
  { rejectValue: KeyedFeatureError }
>(
  'migration/loadActivityTrail',
  async ({ orgId, projectId, planId }, { rejectWithValue }) => {
    try {
      return await migrationService.getActivityTrail(orgId, projectId, planId)
    } catch (error) {
      return rejectWithValue(toMigError(error))
    }
  },
  {
    condition: trailNotInFlight('activity')
  }
)

export const loadReconciliationTrace = createAsyncThunk<
  ReconciliationTraceEntry[],
  PlanArg,
  { rejectValue: KeyedFeatureError }
>(
  'migration/loadReconciliationTrace',
  async ({ orgId, projectId, planId }, { rejectWithValue }) => {
    try {
      return await migrationService.getReconciliationTrace(orgId, projectId, planId)
    } catch (error) {
      return rejectWithValue(toMigError(error))
    }
  },
  {
    condition: trailNotInFlight('trace')
  }
)

export const confirmGuardrails = createAsyncThunk<
  GuardrailStatus,
  PlanArg & { input?: ConfirmGuardrailsInput },
  { rejectValue: KeyedFeatureError }
>(
  'migration/confirmGuardrails',
  async ({ orgId, projectId, planId, input }, { rejectWithValue }) => {
    try {
      return await migrationService.confirmGuardrails(orgId, projectId, planId, input)
    } catch (error) {
      return rejectWithValue(toMigError(error))
    }
  }
)

// ---------------------------------------------------------------------------
// The realtime frame this slice consumes — the EXISTING action, never a new one.
// ---------------------------------------------------------------------------

/** Typed alias of `realtime/invalidateActivity` carrying this feature's frame shape. */
export const migrationBusFrame = createAction<{ projectId: string | null; planId?: string; runId?: string }>(
  'realtime/invalidateActivity'
)

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const migrationSlice = createSlice({
  name: 'migration',
  initialState,
  reducers: {
    /** Leave the workspace — resets everything. */
    migrationClosed(state) {
      state.projectId = null
      state.selectedPlanId = null
      state.selectedRunId = null
      state.plans = []
      state.creatingPlan = false
      state.createPlanError = null
      state.runs = emptySubState()
      state.reconciliation = emptySubState()
      state.goNoGo = emptySubState()
      state.goNoGoMissing = false
      state.rollback = emptySubState()
      state.trail = emptyTrail()
      state.mutating = null
      state.error = null
      state.blocked = null
      state.invalidationNonce = 0
      invalidate(state)
    },
    /** Dismiss the page-level refusal. */
    migrationErrorDismissed(state) {
      state.error = null
    },
    planSelected(state, action: PayloadAction<string | null>) {
      state.selectedPlanId = action.payload
      state.selectedRunId = null
      state.runs = emptySubState()
      state.reconciliation = emptySubState()
      state.goNoGo = emptySubState()
      state.goNoGoMissing = false
      state.rollback = emptySubState()
      state.trail = emptyTrail()
    },
    runSelected(state, action: PayloadAction<string | null>) {
      state.selectedRunId = action.payload
      state.reconciliation = emptySubState()
      state.rollback = emptySubState()
    }
  },
  extraReducers: (builder) => {
    builder
      // ── Plans (the blocked source) ──────────────────────────────────
      .addCase(openMigration.pending, (state, action) => {
        if (state.projectId !== action.meta.arg.projectId) {
          state.plans = []
          state.selectedPlanId = null
          state.selectedRunId = null
        }
        fetchStarted(state, action.meta.arg.projectId)
        state.projectId = action.meta.arg.projectId
        state.error = null
        state.blocked = null
      })
      .addCase(openMigration.fulfilled, (state, action) => {
        // Key-scoped (TASK-3D6DB8B6): a superseded project's reply writes nothing.
        if (!fetchSucceeded(state, action.meta.arg.projectId)) return
        state.plans = action.payload ?? []
      })
      .addCase(openMigration.rejected, (state, action) => {
        if (action.meta.condition) {
          // A VETOED open (TASK-3D6DB8B6 fix cycle 3, the projectDetail
          // precedent): this project's load is in flight or its plans are
          // fresh. `migrationClosed` (StrictMode's between-effects cleanup,
          // or leaving the page mid-load) nulled `projectId`, and the frame
          // guard below then dropped every frame. Re-assert it; the cache is
          // untouched and only an explicit open reaches this branch.
          state.projectId = action.meta.arg.projectId
          return
        }
        if (!fetchFailed(state, action.meta.arg.projectId)) return
        const failure = action.payload
        if (failure === undefined) {
          state.error = unknownError(action.error.message)
          state.blocked = null
          return
        }
        if (failure.status === 403 || failure.status === 404) {
          state.blocked = { status: failure.status, code: failure.code, message: failure.message }
          state.error = null
        } else {
          state.error = failure
          state.blocked = null
        }
      })

      // ── Create plan ──────────────────────────────────────────────────
      .addCase(createMigrationPlan.pending, (state) => {
        state.creatingPlan = true
        state.createPlanError = null
      })
      .addCase(createMigrationPlan.fulfilled, (state, action) => {
        state.creatingPlan = false
        state.plans.push(action.payload)
        state.selectedPlanId = action.payload.id
      })
      .addCase(createMigrationPlan.rejected, (state, action) => {
        state.creatingPlan = false
        state.createPlanError = action.payload ?? unknownError(action.error.message)
      })

      // ── Runs ─────────────────────────────────────────────────────────
      .addCase(loadRuns.pending, (state) => {
        state.runs.loading = true
        state.runs.error = null
      })
      .addCase(loadRuns.fulfilled, (state, action) => {
        state.runs.data = action.payload ?? []
        state.runs.loading = false
      })
      .addCase(loadRuns.rejected, (state, action) => {
        state.runs.loading = false
        state.runs.error = action.payload ?? unknownError(action.error.message)
      })
      .addCase(loadRun.fulfilled, (state, action) => {
        const rows = state.runs.data ?? []
        const index = rows.findIndex((r) => r.id === action.payload.id)
        if (index === -1) rows.push(action.payload)
        else rows[index] = action.payload
        state.runs.data = rows
      })

      // ── Reconciliation ───────────────────────────────────────────────
      .addCase(loadReconciliation.pending, (state) => {
        state.reconciliation.loading = true
        state.reconciliation.error = null
      })
      .addCase(loadReconciliation.fulfilled, (state, action) => {
        state.reconciliation.data = action.payload
        state.reconciliation.loading = false
      })
      .addCase(loadReconciliation.rejected, (state, action) => {
        state.reconciliation.loading = false
        state.reconciliation.error = action.payload ?? unknownError(action.error.message)
      })
      .addCase(assignDifferenceOwner.pending, (state, action) => {
        state.mutating = action.meta.arg.diffId
      })
      .addCase(assignDifferenceOwner.fulfilled, (state, action) => {
        state.mutating = null
        state.reconciliation.data = action.payload
        state.reconciliation.error = null
      })
      .addCase(assignDifferenceOwner.rejected, (state, action) => {
        state.mutating = null
        state.reconciliation.error =
          action.payload ?? { ...unknownError(action.error.message), scope: action.meta.arg.diffId }
      })
      .addCase(proposeDifference.pending, (state, action) => {
        state.mutating = action.meta.arg.diffId
      })
      .addCase(proposeDifference.fulfilled, (state, action) => {
        state.mutating = null
        state.reconciliation.data = action.payload
        state.reconciliation.error = null
      })
      .addCase(proposeDifference.rejected, (state, action) => {
        state.mutating = null
        state.reconciliation.error =
          action.payload ?? { ...unknownError(action.error.message), scope: action.meta.arg.diffId }
      })
      .addCase(disposeDifference.pending, (state, action) => {
        state.mutating = action.meta.arg.diffId
      })
      .addCase(disposeDifference.fulfilled, (state, action) => {
        state.mutating = null
        state.reconciliation.data = action.payload
        state.reconciliation.error = null
      })
      .addCase(disposeDifference.rejected, (state, action) => {
        state.mutating = null
        // Byte-identical state: reconciliation.data is untouched here.
        state.reconciliation.error =
          action.payload ?? { ...unknownError(action.error.message), scope: action.meta.arg.diffId }
      })

      // ── Go/no-go ─────────────────────────────────────────────────────
      .addCase(loadGoNoGo.pending, (state) => {
        state.goNoGo.loading = true
      })
      .addCase(loadGoNoGo.fulfilled, (state, action) => {
        state.goNoGo.data = action.payload
        state.goNoGo.loading = false
        state.goNoGo.error = null
        state.goNoGoMissing = false
      })
      .addCase(loadGoNoGo.rejected, (state, action) => {
        state.goNoGo.loading = false
        const failure = action.payload
        if (failure !== undefined && failure.status === 404) {
          // Decision 8: absence of a row, not an error and not `blocked`.
          state.goNoGoMissing = true
          state.goNoGo.data = null
          state.goNoGo.error = null
          return
        }
        state.goNoGo.error = failure ?? unknownError(action.error.message)
      })
      .addCase(recordGoNoGo.pending, (state) => {
        state.mutating = 'go-no-go-record'
        // Clear only a PRIOR record refusal — a read failure (`scope`
        // `undefined`) is not this action's to clear (deferred-findings AC-1).
        if (state.goNoGo.error?.scope === 'record') state.goNoGo.error = null
      })
      .addCase(recordGoNoGo.fulfilled, (state) => {
        state.mutating = null
      })
      .addCase(recordGoNoGo.rejected, (state, action) => {
        state.mutating = null
        // Scoped so the panel can keep rendering the stored decision instead
        // of replacing it with the error panel (deferred-findings AC-1).
        state.goNoGo.error = { ...(action.payload ?? unknownError(action.error.message)), scope: 'record' }
      })

      // ── Rollback ─────────────────────────────────────────────────────
      .addCase(loadRollback.pending, (state) => {
        state.rollback.loading = true
      })
      .addCase(loadRollback.fulfilled, (state, action) => {
        state.rollback.data = action.payload
        state.rollback.loading = false
        state.rollback.error = null
      })
      .addCase(loadRollback.rejected, (state, action) => {
        state.rollback.loading = false
        state.rollback.error = action.payload ?? unknownError(action.error.message)
      })
      .addCase(executeRollback.pending, (state) => {
        state.mutating = 'rollback-execute'
        state.rollback.error = null
      })
      .addCase(executeRollback.fulfilled, (state, action) => {
        state.mutating = null
        state.rollback.data = action.payload
      })
      .addCase(executeRollback.rejected, (state, action) => {
        state.mutating = null
        state.rollback.error = action.payload ?? unknownError(action.error.message)
      })

      // ── Trail ────────────────────────────────────────────────────────
      // Each read's `trailInFlight[part]` is set to the requesting planId on
      // `pending` and cleared ONLY if it still names that same planId on
      // settlement — a later dispatch for a DIFFERENT plan must never have
      // its own in-flight flag wiped by an earlier request's straggling
      // response (deferred-findings AC-2).
      .addCase(loadTrailSummary.pending, (state, action) => {
        state.trail.summary.loading = true
        state.trailInFlight.summary = action.meta.arg.planId
      })
      .addCase(loadTrailSummary.fulfilled, (state, action) => {
        state.trail.summary.data = action.payload
        state.trail.summary.loading = false
        state.trail.summary.error = null
        settleTrail(state, 'summary', action.meta.arg.planId)
      })
      .addCase(loadTrailSummary.rejected, (state, action) => {
        state.trail.summary.loading = false
        state.trail.summary.error = action.payload ?? unknownError(action.error.message)
        settleTrail(state, 'summary', action.meta.arg.planId)
      })
      .addCase(loadAuditTrail.pending, (state, action) => {
        state.trail.audit.loading = true
        if (action.meta.arg.cursor === undefined) state.trailInFlight.audit = action.meta.arg.planId
      })
      .addCase(loadAuditTrail.fulfilled, (state, action) => {
        state.trail.audit.data = action.payload
        state.trail.audit.loading = false
        state.trail.audit.error = null
        // A cursor (next-page) request never set the in-flight marker — never clear it.
        if (action.meta.arg.cursor === undefined) settleTrail(state, 'audit', action.meta.arg.planId)
      })
      .addCase(loadAuditTrail.rejected, (state, action) => {
        state.trail.audit.loading = false
        state.trail.audit.error = action.payload ?? unknownError(action.error.message)
        // A cursor (next-page) request never set the in-flight marker — never clear it.
        if (action.meta.arg.cursor === undefined) settleTrail(state, 'audit', action.meta.arg.planId)
      })
      .addCase(loadActivityTrail.pending, (state, action) => {
        state.trail.activity.loading = true
        state.trailInFlight.activity = action.meta.arg.planId
      })
      .addCase(loadActivityTrail.fulfilled, (state, action) => {
        state.trail.activity.data = action.payload ?? []
        state.trail.activity.loading = false
        state.trail.activity.error = null
        settleTrail(state, 'activity', action.meta.arg.planId)
      })
      .addCase(loadActivityTrail.rejected, (state, action) => {
        state.trail.activity.loading = false
        state.trail.activity.error = action.payload ?? unknownError(action.error.message)
        settleTrail(state, 'activity', action.meta.arg.planId)
      })
      .addCase(loadReconciliationTrace.pending, (state, action) => {
        state.trail.trace.loading = true
        state.trailInFlight.trace = action.meta.arg.planId
      })
      .addCase(loadReconciliationTrace.fulfilled, (state, action) => {
        state.trail.trace.data = action.payload ?? []
        state.trail.trace.loading = false
        state.trail.trace.error = null
        settleTrail(state, 'trace', action.meta.arg.planId)
      })
      .addCase(loadReconciliationTrace.rejected, (state, action) => {
        state.trail.trace.loading = false
        state.trail.trace.error = action.payload ?? unknownError(action.error.message)
        settleTrail(state, 'trace', action.meta.arg.planId)
      })
      .addCase(confirmGuardrails.pending, (state) => {
        state.mutating = 'guardrails-confirm'
        state.trail.guardrailError = null
      })
      .addCase(confirmGuardrails.fulfilled, (state, action) => {
        state.mutating = null
        if (state.trail.summary.data !== null) {
          state.trail.summary.data.guardrailStatus = action.payload
        }
      })
      .addCase(confirmGuardrails.rejected, (state, action) => {
        state.mutating = null
        state.trail.guardrailError = action.payload ?? unknownError(action.error.message)
      })

      // ── Realtime (Decision 12, INV-5.7) ─────────────────────────────
      .addCase(migrationBusFrame, (state, action) => {
        const { projectId } = action.payload
        // A frame with no project tuple (`null`) is a user-level frame, not
        // this project's — `null !== null` would otherwise pass the
        // comparison below (deferred-findings AC-4).
        if (projectId === null || projectId !== state.projectId) return
        // A server change: mid-flight it earns one follow-up load (TASK-3D6DB8B6).
        state.invalidationNonce += 1
        markServerChanged(state)
      })
  }
})

export const { migrationClosed, migrationErrorDismissed, planSelected, runSelected } = migrationSlice.actions

/* ── Selectors ─────────────────────────────────────────────────────────── */

interface WithMigration {
  migration: MigrationState
}

/**
 * One stable identity for "no runs loaded yet". A fresh `[]` per call makes
 * react-redux warn (and re-render) on every read while `runs.data` is null.
 */
const NO_RUNS: MigrationRunRow[] = []

export const selectMigrationPlans = (s: WithMigration): MigrationPlanRow[] => s.migration.plans
export const selectSelectedPlanId = (s: WithMigration): string | null => s.migration.selectedPlanId
export const selectSelectedRunId = (s: WithMigration): string | null => s.migration.selectedRunId
export const selectMigrationRuns = (s: WithMigration): MigrationRunRow[] => s.migration.runs.data ?? NO_RUNS
export const selectRunsLoading = (s: WithMigration): boolean => s.migration.runs.loading
export const selectRunsError = (s: WithMigration): KeyedFeatureError | null => s.migration.runs.error

export const selectReconciliationReport = (s: WithMigration): ReconciliationReport | null =>
  s.migration.reconciliation.data
export const selectReconciliationLoading = (s: WithMigration): boolean => s.migration.reconciliation.loading
export const selectReconciliationError = (s: WithMigration): KeyedFeatureError | null =>
  s.migration.reconciliation.error

export const selectGoNoGo = (s: WithMigration): GoNoGoRecord | null => s.migration.goNoGo.data
export const selectGoNoGoLoading = (s: WithMigration): boolean => s.migration.goNoGo.loading
export const selectGoNoGoError = (s: WithMigration): KeyedFeatureError | null => s.migration.goNoGo.error
export const selectGoNoGoMissing = (s: WithMigration): boolean => s.migration.goNoGoMissing

export const selectRollbackState = (s: WithMigration): RollbackStateView | null => s.migration.rollback.data
export const selectRollbackLoading = (s: WithMigration): boolean => s.migration.rollback.loading
export const selectRollbackError = (s: WithMigration): KeyedFeatureError | null => s.migration.rollback.error

export const selectMigrationTrail = (s: WithMigration): TrailState => s.migration.trail

export const selectMigrationLoading = (s: WithMigration): boolean => s.migration.loading
export const selectMigrationError = (s: WithMigration): KeyedFeatureError | null => s.migration.error
export const selectMigrationBlocked = (s: WithMigration): FeatureBlocked | null => s.migration.blocked
export const selectMigrationNonce = (s: WithMigration): number => s.migration.invalidationNonce
export const selectMigrationMutating = (s: WithMigration): string | null => s.migration.mutating
export const selectCreatingPlan = (s: WithMigration): boolean => s.migration.creatingPlan
export const selectCreatePlanError = (s: WithMigration): KeyedFeatureError | null => s.migration.createPlanError
